// src/prototype/components/oir/report/OIRSectionFinancialImpact.tsx

import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { sumScenarioYear1Millions } from '../../../roi/hubFigures';
import { DollarSign, Clock, TrendingDown, AlertCircle, BarChart2 } from 'lucide-react';

interface Opportunity {
  id: string;
  roiEstimate: { low: number; high: number; unit?: string };
  timeToValue: string;
}

interface OIRSectionFinancialImpactProps {
  top10: Opportunity[];
  selectedOpportunities: Opportunity[];
  portfolioRangeLabel: string;
  shortlistedRangeLabel: string;
}

const avgMonths = (opps: Opportunity[]): number => {
  if (opps.length === 0) return 0;
  const months = opps.map((o) => parseInt(o.timeToValue.split('-')[0]) || 12);
  return Math.round(months.reduce((a, b) => a + b, 0) / months.length);
};

/**
 * The waterfall rows for a portfolio under an engine scenario. YEAR 1 throughout: realized
 * (ramp-adjusted) benefit, year-1 cost (implementation + first-year operating), and the
 * engine's own net. No multipliers, no cost-as-a-percentage-of-benefit.
 */
export function buildFinancialWaterfall(ids: readonly string[], scenario: 'conservative' | 'base' | 'aggressive') {
  const { benefit, cost, net } = sumScenarioYear1Millions(ids, scenario);
  const fmt = (m: number) => `$${m.toFixed(1)}M`;
  return [
    { name: 'Baseline', value: 0, displayValue: '$0', fill: '#e5e7eb', type: 'baseline' },
    { name: 'Year-1 benefit', value: benefit, displayValue: `+${fmt(benefit)}`, fill: '#18365E', type: 'benefit' },
    { name: 'Year-1 cost', value: -cost, displayValue: `-${fmt(cost)}`, fill: '#EF4444', type: 'cost' },
    { name: 'Year-1 net', value: net, displayValue: fmt(net), fill: '#FF6E4C', type: 'net' },
  ];
}

export const OIRSectionFinancialImpact: React.FC<OIRSectionFinancialImpactProps> = ({
  top10,
  selectedOpportunities,
  portfolioRangeLabel,
  shortlistedRangeLabel,
}) => {
  const [scenario, setScenario] = useState<'conservative' | 'base' | 'aggressive'>('base');

  const top10AvgMonths = useMemo(() => avgMonths(top10), [top10]);
  const selectedAvgMonths = useMemo(
    () => (selectedOpportunities.length > 0 ? avgMonths(selectedOpportunities) : null),
    [selectedOpportunities]
  );
  const displayAvgMonths = selectedAvgMonths ?? top10AvgMonths;

  const activeOpps = selectedOpportunities.length > 0 ? selectedOpportunities : top10;

  const waterfall = useMemo(
    () => buildFinancialWaterfall(activeOpps.map((o) => o.id), scenario),
    [activeOpps, scenario],
  );

  const kpis = [
    {
      icon: DollarSign,
      label: 'Top 10 Portfolio Value',
      value: portfolioRangeLabel,
      sub: 'annual value potential',
      accent: 'text-[#FF6E4C]',
      bg: 'bg-[#FF6E4C]/8',
    },
    {
      icon: TrendingDown,
      label: 'Selected Portfolio Value',
      value: selectedOpportunities.length > 0 ? shortlistedRangeLabel : '—',
      sub:
        selectedOpportunities.length > 0
          ? `${selectedOpportunities.length} selected initiatives`
          : 'No initiatives selected',
      accent: selectedOpportunities.length > 0 ? 'text-[#18365E]' : 'text-[#717182]',
      bg: selectedOpportunities.length > 0 ? 'bg-[#18365E]/8' : 'bg-[#F5F7FA]',
    },
    {
      icon: Clock,
      label: 'Avg Time to Value',
      value: `${displayAvgMonths} mo`,
      sub:
        selectedOpportunities.length > 0 ? 'selected portfolio avg' : 'top 10 portfolio avg',
      accent: 'text-[#18365E]',
      bg: 'bg-[#18365E]/8',
    },
  ];

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">
      {/* Section header */}
      <div className="mb-4">
        <h2 className="text-base font-bold text-[#18365E]">Financial Impact</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">
          Value roll-up, payback estimates, and scenario sensitivity for the evaluated portfolio
        </p>
      </div>

      {/* Narrative block */}
      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
        The top 10 initiatives represent <strong>{portfolioRangeLabel}</strong> in annual value
        potential.{' '}
        {selectedOpportunities.length > 0 ? (
          <>
            The selected portfolio of <strong>{selectedOpportunities.length}</strong> initiative
            {selectedOpportunities.length !== 1 ? 's' : ''} targets{' '}
            <strong>{shortlistedRangeLabel}</strong> annually.{' '}
          </>
        ) : null}
        Payback periods are estimated at <strong>{displayAvgMonths} months</strong> on average
        based on time-to-value proxies, with variation by implementation complexity.
      </p>

      {/* 3-card roll-up */}
      <div className="grid grid-cols-1 gap-3 mb-5 sm:grid-cols-3">
        {kpis.map((kpi) => (
          <div key={kpi.label} className={`${kpi.bg} rounded-xl p-4 flex flex-col gap-1`}>
            <div className="flex items-center gap-1.5 mb-1">
              <kpi.icon className={`w-3.5 h-3.5 ${kpi.accent}`} />
              <span className="text-[9px] font-semibold text-[#717182] uppercase tracking-wide">
                {kpi.label}
              </span>
            </div>
            <p className={`text-lg font-bold ${kpi.accent} leading-tight`}>{kpi.value}</p>
            <p className="text-[10px] text-[#717182]">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Scenario toggle + waterfall */}
      <div className="mb-4 rounded-xl border border-[#e5e7eb] bg-[#F5F7FA] p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-[#18365E]/60" />
            <p className="text-xs font-semibold text-[#18365E]">Value Realization Waterfall</p>
          </div>

          <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] rounded-lg p-1 w-fit">
            {([
              { id: 'conservative', label: 'Conservative' },
              { id: 'base', label: 'Base' },
              { id: 'aggressive', label: 'Aggressive' },
            ] as const).map((opt) => {
              const isActive = scenario === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setScenario(opt.id)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
                    isActive
                      ? 'bg-[#18365E] text-white'
                      : 'text-[#18365E]/70 hover:text-[#18365E] hover:bg-[#18365E]/[0.06]'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-[220px] w-full bg-white rounded-xl border border-[#e5e7eb] p-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={waterfall} margin={{ top: 14, right: 10, bottom: 6, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: '#717182' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#717182' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
                label={{ value: '($M, year 1)', angle: -90, position: 'insideLeft', fill: '#717182', fontSize: 10 }}
              />
              <ReferenceLine y={0} stroke="#e5e7eb" />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d: any = payload[0].payload;
                  return (
                    <div className="bg-white border border-[#e5e7eb] rounded-lg shadow-lg px-3 py-2">
                      <p className="text-xs font-semibold text-[#18365E]">{d.name}</p>
                      <p className="text-[11px] text-[#717182] mt-0.5">{d.displayValue}</p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {waterfall.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
                <LabelList
                  dataKey="displayValue"
                  position="top"
                  style={{ fontSize: 10, fill: '#18365E', fontWeight: 700 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sensitivity callout */}
      <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl p-3.5">
        <AlertCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-blue-800 mb-0.5">Scenario Sensitivity</p>
          <p className="text-[11px] text-blue-700 leading-relaxed">
            Toggle scenarios to stress-test year-1 value and cost. Conservative and Aggressive use each
            initiative's own scenario inputs from the ROI model.
          </p>
        </div>
      </div>
    </div>
  );
};